import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalKeyTrap } from '../../hooks/useModalKeyTrap';
import './TrailerModal.css';

interface TrailerModalProps {
  trailerKey: string;
  onClose: () => void;
}

export const TrailerModal: React.FC<TrailerModalProps> = ({ trailerKey, onClose }) => {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [embedError, setEmbedError] = useState(false);
  // Local toggle — may drift if user uses YouTube's own UI, but good enough for remote control
  const playingRef = useRef(true);

  const sendCommand = (func: string) => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func }),
      '*',
    );
  };

  useModalKeyTrap(useCallback(function (key, e) {
    if (key === 'Back') {
      e.preventDefault();
      sendCommand('stopVideo');
      onClose();
    } else if (key === 'Enter' || key === 'Space' || key === 'MediaPlayPause') {
      // Toggle play/pause via Worker relay (ignored by Worker if video not ready yet)
      e.preventDefault();
      if (playingRef.current) {
        sendCommand('pauseVideo');
        playingRef.current = false;
      } else {
        sendCommand('playVideo');
        playingRef.current = true;
      }
    }
  }, [onClose]));

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!e.data) return;
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (data?.event === 'onError') setEmbedError(true);
      } catch { /* noop */ }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <div className="trailer-modal" onClick={onClose}>
      <div className="trailer-modal__container" onClick={(e) => e.stopPropagation()}>
        <button className="trailer-modal__close" onClick={onClose} aria-label={t('common.close')}>✕</button>
        {embedError ? (
          <div className="trailer-modal__error">
            <span className="trailer-modal__error-text">{t('trailerModal.unavailable')}</span>
            <button className="trailer-modal__error-btn" onClick={onClose}>{t('common.close')}</button>
          </div>
        ) : (
          <>
            <iframe
              ref={iframeRef}
              className="trailer-modal__iframe"
              src={`https://ott-player-trailer.enrico-mendonca.workers.dev/?v=${trailerKey}&mode=full`}
              title={t('trailerModal.player')}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </>
        )}
      </div>
    </div>
  );
};
